// Parses the templated product description markdown into typed sections
// so ProductDescription.tsx can render each section with its own layout.

export type SectionId =
  | 'overview'
  | 'mechanism'
  | 'effects'
  | 'dosage'
  | 'sideEffects'
  | 'precautions'
  | 'metadata'
  | 'storage'
  | 'disclaimer'
  | 'other';

export interface ParsedSection {
  id: SectionId;
  title: string;
  content: string;
}

const TITLE_MAP: Record<string, SectionId> = {
  'Product Overview': 'overview',
  'How It Works': 'mechanism',
  'Effects': 'effects',
  'Dosage': 'dosage',
  'Side Effects': 'sideEffects',
  'Precautions': 'precautions',
  'Manufacturer/Ingredients': 'metadata',
};

const SECTION_ORDER: SectionId[] = [
  'overview',
  'mechanism',
  'effects',
  'dosage',
  'sideEffects',
  'precautions',
  'storage',
  'metadata',
];

export function parseDescription(description: string | null | undefined): ParsedSection[] {
  if (!description) return [];

  // Strip trailing italic disclaimer block (prefixed with underscore)
  // e.g. "_Acivir Cream is a prescription medication..._"
  const disclaimerMatch = description.match(/\n\n_([^]+?)_\s*$/);
  let body = description;
  let disclaimer: ParsedSection | null = null;
  if (disclaimerMatch) {
    body = description.slice(0, disclaimerMatch.index).trim();
    disclaimer = { id: 'disclaimer', title: 'Important Notice', content: disclaimerMatch[1].trim() };
  }

  // Split on top-level H2 markers
  const parts = body.split(/\n(?=## )/);
  const sections: ParsedSection[] = [];
  for (const part of parts) {
    const match = part.match(/^## (.+?)\n([\s\S]*)$/);
    if (!match) continue;
    const title = match[1].trim();
    const content = match[2].trim();
    const id = TITLE_MAP[title] || 'other';
    sections.push({ id, title, content });
  }

  if (disclaimer) sections.push(disclaimer);
  return sections;
}

export function sortSections(sections: ParsedSection[]): ParsedSection[] {
  const orderMap = new Map(SECTION_ORDER.map((id, i) => [id, i]));
  return [...sections].sort((a, b) => {
    const ai = orderMap.get(a.id) ?? 99;
    const bi = orderMap.get(b.id) ?? 99;
    return ai - bi;
  });
}

// Parse "Onset" + "Duration" out of the Effects section content
export function parseEffectsTiming(content: string): { onset: string; duration: string } {
  const onset = content.match(/\*\*Onset:\*\*\s*([^\n]+)/)?.[1]?.trim() || '';
  const duration = content.match(/\*\*Duration:\*\*\s*([^\n]+)/)?.[1]?.trim() || '';
  return { onset, duration };
}

// Parse "- **Key:** Value" pairs from metadata section
export interface MetaPair { key: string; value: string }
export function parseMetadataPairs(content: string): MetaPair[] {
  const pairs: MetaPair[] = [];
  for (const line of content.split('\n')) {
    const m = line.match(/^\s*-\s*\*\*(.+?):\*\*\s*(.+?)\s*$/);
    if (m) pairs.push({ key: m[1].trim(), value: m[2].trim() });
  }
  return pairs;
}

// Split a section content into a lead paragraph (any text before bullets)
// and an array of bullet items
export function splitLeadAndBullets(content: string): { lead: string; bullets: string[] } {
  const lines = content.split('\n');
  const leadLines: string[] = [];
  const bulletLines: string[] = [];
  let inBullets = false;
  for (const line of lines) {
    if (line.startsWith('- ')) {
      inBullets = true;
      bulletLines.push(line.slice(2).trim());
    } else if (!inBullets && line.trim()) {
      leadLines.push(line.trim());
    }
  }
  return { lead: leadLines.join(' '), bullets: bulletLines };
}
